import defaultApiClient, { tryCatch } from '@/services/api-client';
import { persistStorageService } from '@/features/auth/persist-storage';
import {
  ConversationsOverviewResponse,
  DashboardFilters,
  DashboardStatsResponse,
  InteractionsUsageResponse,
  SentimentAnalysisResponse,
  TotalCountResponse,
} from './dashboard.types';

const buildQuery = (params: Record<string, unknown>) => {
  const q = new URLSearchParams();
  Object.entries(params).forEach(([k, v]) => {
    if (v === undefined || v === null || v === '') return;
    q.append(k, String(v));
  });
  const s = q.toString();
  return s ? `?${s}` : '';
};

const getOrgLocFromStorage = () => {
  const selectedOrganisation = persistStorageService.getPersistItem<{
    id: string;
    name: string;
  }>({ name: 'selectedOrganisation', parse: true });
  const selectedLocation = persistStorageService.getPersistItem<{
    id: string;
    name: string;
  }>({ name: 'selectedLocation', parse: true });
  return {
    organisation_id: selectedOrganisation?.id,
    location_id: selectedLocation?.id,
  } as const;
};

export const getDashboardStats = async (
  filters: DashboardFilters,
): Promise<DashboardStatsResponse> => {
  const orgLoc = getOrgLocFromStorage();
  const query = buildQuery({ ...filters, ...orgLoc });
  const { data: response, error } = await tryCatch(
    defaultApiClient.get<{ data: DashboardStatsResponse }>(
      `/api/v1/dashboard/stats${query}`,
    ),
  );
  if (error) throw error;
  return response.data.data;
};

export const getTotalConversations = async (
  filters: DashboardFilters,
): Promise<TotalCountResponse> => {
  const orgLoc = getOrgLocFromStorage();
  const query = buildQuery({ ...filters, ...orgLoc });
  const { data: response, error } = await tryCatch(
    defaultApiClient.get<{ data: TotalCountResponse }>(
      `/api/v1/dashboard/total-conversations${query}`,
    ),
  );
  if (error) throw error;
  return response.data.data;
};

export const getTotalMessages = async (
  filters: DashboardFilters,
): Promise<TotalCountResponse> => {
  const orgLoc = getOrgLocFromStorage();
  const query = buildQuery({ ...filters, ...orgLoc });
  const { data: response, error } = await tryCatch(
    defaultApiClient.get<{ data: TotalCountResponse }>(
      `/api/v1/dashboard/total-messages${query}`,
    ),
  );
  if (error) throw error;
  return response.data.data;
};

export const getTotalUsers = async (
  filters: DashboardFilters,
): Promise<TotalCountResponse> => {
  const orgLoc = getOrgLocFromStorage();
  const query = buildQuery({ ...filters, ...orgLoc });
  const { data: response, error } = await tryCatch(
    defaultApiClient.get<{ data: TotalCountResponse }>(
      `/api/v1/dashboard/total-users${query}`,
    ),
  );
  if (error) throw error;
  return response.data.data;
};

// Normalize conversations overview to a predictable shape for the chart
export const getConversationsOverview = async (
  filters: DashboardFilters,
): Promise<ConversationsOverviewResponse> => {
  const orgLoc = getOrgLocFromStorage();
  const query = buildQuery({ ...filters, ...orgLoc });
  const { data: response, error } = await tryCatch(
    defaultApiClient.get<any>(
      `/api/v1/dashboard/conversations-overview${query}`,
    ),
  );
  if (error) throw error;

  const raw = response.data.data;

  // Try to support a few possible backend shapes safely
  let points: ConversationsOverviewResponse['points'] = [];

  if (Array.isArray(raw?.points)) {
    points = raw.points as ConversationsOverviewResponse['points'];
  } else if (Array.isArray(raw)) {
    points = raw as ConversationsOverviewResponse['points'];
  } else if (raw?.dates && (raw?.web || raw?.whatsapp)) {
    const dates: string[] = raw.dates;
    points = dates.map((d: string, idx: number) => ({
      date: d,
      web: Array.isArray(raw.web) ? raw.web[idx] : undefined,
      whatsapp: Array.isArray(raw.whatsapp) ? raw.whatsapp[idx] : undefined,
    }));
  }

  return { points };
};

export const getSentimentAnalysis = async (
  filters: DashboardFilters,
): Promise<SentimentAnalysisResponse> => {
  const orgLoc = getOrgLocFromStorage();
  const query = buildQuery({ ...filters, ...orgLoc });
  const { data: response, error } = await tryCatch(
    defaultApiClient.get<{ data: SentimentAnalysisResponse }>(
      `/api/v1/dashboard/sentiment-analysis${query}`,
    ),
  );
  if (error) throw error;
  return response.data.data;
};

export const getInteractionsUsage = async (
  filters: DashboardFilters,
): Promise<InteractionsUsageResponse> => {
  const orgLoc = getOrgLocFromStorage();
  const query = buildQuery({ ...filters, ...orgLoc });
  const { data: response, error } = await tryCatch(
    defaultApiClient.get<{ data: InteractionsUsageResponse }>(
      `/api/v1/dashboard/interactions-usage${query}`,
    ),
  );
  if (error) throw error;
  return response.data.data;
};

export const dashboardService = {
  getDashboardStats,
  getTotalConversations,
  getTotalMessages,
  getTotalUsers,
  getConversationsOverview,
  getSentimentAnalysis,
  getInteractionsUsage,
};
